import { AICoachReviewsMode, AICoachReviewsTone, AICoachReviewsLength } from "common/src/gamePage/types";
import { AICoachReviewError, InsufficientBalanceError } from "common/src/gamePage/errors/ai-coach.errors";

// Request
interface BaseReviewRequest {
	gameId: number;
	teamId: number;
	tone: AICoachReviewsTone;
	length: AICoachReviewsLength;
}

interface AllModeReviewRequest extends BaseReviewRequest {
	mode: AICoachReviewsMode.All;
	excludePlayerIds?: number[];
	excludeTeamReview?: boolean;
}

interface IndividualReviewRequest extends BaseReviewRequest {
	reviewText?: string;
	attachedEventIds?: number[];
}

interface TeamModeReviewRequest extends IndividualReviewRequest {
	mode: AICoachReviewsMode.Team;
}

interface PlayerModeReviewRequest extends IndividualReviewRequest {
	mode: AICoachReviewsMode.Player;
	playerId: number;
}

export type AICoachReviewRequestPayload = AllModeReviewRequest | TeamModeReviewRequest | PlayerModeReviewRequest;

interface AICoachReviewRequest {
	payload: AICoachReviewRequestPayload;
	accessToken: string;
}

// Response
interface BaseReviewResponse extends BaseReviewRequest {
	excludedCount: number;
}

interface ReviewIdentity {
	label: string;
	contentId: string | null;
}

type ReviewOutcome = { ok: true; text: string } | { ok: false; error: string };

type ReviewContents = ReviewIdentity & ReviewOutcome;

type PlayerReviewContents = ReviewContents & {
	playerId: number | null;
	gamePlayerId: number;
};

interface AllModeReviewResponse extends BaseReviewResponse {
	mode: AICoachReviewsMode.All;
	teamReview: ReviewContents | null; // teamReview is null when excludeTeamReview is true
	playerReviews: PlayerReviewContents[];
}

interface TeamModeReviewResponse extends BaseReviewResponse {
	mode: AICoachReviewsMode.Team;
	teamReview: ReviewContents;
	playerReviews: [];
}

interface PlayerModeReviewResponse extends BaseReviewResponse {
	mode: AICoachReviewsMode.Player;
	teamReview: null;
	playerReviews: PlayerReviewContents[];
}

type AICoachReviewResponsePayload = AllModeReviewResponse | TeamModeReviewResponse | PlayerModeReviewResponse;

interface AICoachReviewSuccessResponse {
	success: true;
	data: AICoachReviewResponsePayload;
}

interface AICoachReviewErrorResponse {
	success: false;
	error: { message: string };
}

type AICoachReviewResponse = AICoachReviewSuccessResponse | AICoachReviewErrorResponse;

const BASE_URL = import.meta.env.VITE_AI_LAMBDA_URL;

export const generateAIReview = async ({ payload, accessToken }: AICoachReviewRequest): Promise<AICoachReviewSuccessResponse> => {
	const res = await fetch(`${BASE_URL}/ai/coach/review`, {
		method: "POST",
		headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
		body: JSON.stringify(payload),
	});

	if (res.status === 409) {
		throw new InsufficientBalanceError();
	}

	if (!res.ok) {
		throw new AICoachReviewError(`Failed to generate AI coach review: ${res.status} ${res.statusText}`, res.status);
	}

	const data: AICoachReviewResponse = await res.json();

	if (!data.success) {
		throw new AICoachReviewError(data.error.message);
	}

	return data;
};
